<div class="crm-page shipment-overview-page">
  <app-header
    [title]="(view$ | async)?.header?.number ?? ('COMPONENTS.SHIPMENTS.TITLE' | translate)"
    [subtitle]="'COMPONENTS.SHIPMENTS.DETAIL_SUBTITLE' | translate"
  >
    <div header-actions class="crm-page__header-actions">
      <a
        class="crm-page__action-button"
        [routerLink]="['/companies', companyId, 'crm', 'shipments']"
      >
        ← {{ "COMPONENTS.SHIPMENTS.BUTTONS.BACK" | translate }}
      </a>
    </div>
  </app-header>

  <div class="crm-page__content px-4 py-6 md:px-8 md:py-8">
    @if (isLoading) {
      <div class="crm-page__overlay">
        <div class="shipment-overview-page__spinner" aria-hidden="true"></div>
        <span class="sr-only">{{ "MESSAGES.LOADING" | translate }}</span>
      </div>
    }

    @if (errorMessageKey) {
      <app-card-shell padding="lg">
        <div class="shipment-overview-page__error">
          <div>
            <h2 class="shipment-overview-page__error-title">
              {{ "ERRORS.UNKNOWN_ERROR" | translate }}
            </h2>
            <p class="shipment-overview-page__error-text">{{ errorMessageKey | translate }}</p>
          </div>
          <button type="button" class="crm-page__action-button" (click)="retry()">
            {{ "BUTTONS.RETRY" | translate }}
          </button>
        </div>
      </app-card-shell>
    } @else if (view$ | async; as view) {
      <app-card-shell padding="lg" class="shipment-overview-page__hero">
        <div class="shipment-overview-page__hero-row">
          <div class="shipment-overview-page__hero-meta">
            <h2 class="shipment-overview-page__hero-number">{{ view.header.number }}</h2>
            <span
              class="shipment-overview-page__status-badge"
              [class.shipment-overview-page__status-badge--success]="view.header.statusBadgeKey === 'success'"
              [class.shipment-overview-page__status-badge--warning]="view.header.statusBadgeKey === 'warning'"
            >
              {{ view.header.statusLabelKey | translate }}
            </span>
          </div>

          <dl class="shipment-overview-page__hero-grid">
            <div>
              <dt>{{ "COMPONENTS.SHIPMENTS.FIELDS.DATE" | translate }}</dt>
              <dd>{{ view.header.shipmentDate ? (view.header.shipmentDate | date: "yyyy-MM-dd") : "-" }}</dd>
            </div>
            <div>
              <dt>{{ "COMPONENTS.SHIPMENTS.FIELDS.CUSTOMER" | translate }}</dt>
              <dd>{{ view.header.customerLabel }}</dd>
            </div>
            <div>
              <dt>{{ "COMPONENTS.SHIPMENTS.FIELDS.ORDER" | translate }}</dt>
              <dd>
                @if (view.header.orderId) {
                  <a [routerLink]="['/companies', companyId, 'crm', 'sales-orders', view.header.orderId]">
                    {{ view.header.orderLabel }}
                  </a>
                } @else {
                  {{ view.header.orderLabel }}
                }
              </dd>
            </div>
          </dl>
        </div>

        <div class="shipment-overview-page__actions">
          @if (view.canMarkShipped) {
            <button
              type="button"
              class="form-primary-btn"
              [disabled]="isMutating"
              (click)="openMarkShippedModal()"
            >
              {{ "COMPONENTS.SHIPMENTS.BUTTONS.MARK_SHIPPED" | translate }}
            </button>
          }
          @if (view.canDelete) {
            <button
              type="button"
              class="shipment-overview-page__danger-btn"
              [disabled]="isMutating"
              (click)="openDeleteModal()"
            >
              {{ "COMPONENTS.SHIPMENTS.BUTTONS.DELETE" | translate }}
            </button>
          }
        </div>

        @if (actionErrorMessageKey) {
          <p class="shipment-overview-page__action-error" role="alert">
            {{ actionErrorMessageKey | translate }}
          </p>
        }
      </app-card-shell>

      <app-card-shell padding="none" class="shipment-overview-page__lines-card">
        <div class="crm-table__desktop">
          <div class="crm-table__scroller">
            <table class="crm-table__table">
              <thead>
                <tr class="crm-table__header-row">
                  <th class="crm-table__header-cell">
                    {{ "COMPONENTS.SHIPMENTS.LINES_TABLE.ITEM" | translate }}
                  </th>
                  <th class="crm-table__header-cell">
                    {{ "COMPONENTS.SHIPMENTS.LINES_TABLE.WAREHOUSE" | translate }}
                  </th>
                  <th class="crm-table__header-cell">
                    {{ "COMPONENTS.SHIPMENTS.LINES_TABLE.QUANTITY" | translate }}
                  </th>
                  <th class="crm-table__header-cell">
                    {{ "COMPONENTS.SHIPMENTS.LINES_TABLE.BASE_QUANTITY" | translate }}
                  </th>
                </tr>
              </thead>

              <tbody>
                @if (view.lines.length === 0) {
                  <tr class="crm-table__row">
                    <td class="crm-table__cell" colspan="4">
                      {{ "COMPONENTS.SHIPMENTS.LINES_TABLE.EMPTY" | translate }}
                    </td>
                  </tr>
                } @else {
                  @for (line of view.lines; track line.id) {
                    <tr class="crm-table__row">
                      <td class="crm-table__cell">{{ line.itemLabel }}</td>
                      <td class="crm-table__cell">{{ line.warehouseLabel }}</td>
                      <td class="crm-table__cell">{{ line.quantity | number: "1.0-4" }}</td>
                      <td class="crm-table__cell">{{ line.baseQuantity | number: "1.0-4" }}</td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>
        </div>

        <div class="shipment-overview-page__lines-mobile">
          @if (view.lines.length === 0) {
            <p class="shipment-overview-page__empty">
              {{ "COMPONENTS.SHIPMENTS.LINES_TABLE.EMPTY" | translate }}
            </p>
          } @else {
            @for (line of view.lines; track line.id) {
              <article class="crm-mobile-card">
                <div class="crm-mobile-card__grid">
                  <div class="crm-mobile-card__field">
                    <p class="crm-mobile-card__label">
                      {{ "COMPONENTS.SHIPMENTS.LINES_TABLE.ITEM" | translate }}
                    </p>
                    <p class="crm-mobile-card__value">{{ line.itemLabel }}</p>
                  </div>
                  <div class="crm-mobile-card__field">
                    <p class="crm-mobile-card__label">
                      {{ "COMPONENTS.SHIPMENTS.LINES_TABLE.WAREHOUSE" | translate }}
                    </p>
                    <p class="crm-mobile-card__value">{{ line.warehouseLabel }}</p>
                  </div>
                  <div class="crm-mobile-card__field">
                    <p class="crm-mobile-card__label">
                      {{ "COMPONENTS.SHIPMENTS.LINES_TABLE.QUANTITY" | translate }}
                    </p>
                    <p class="crm-mobile-card__value">{{ line.quantity | number: "1.0-4" }}</p>
                  </div>
                  <div class="crm-mobile-card__field">
                    <p class="crm-mobile-card__label">
                      {{ "COMPONENTS.SHIPMENTS.LINES_TABLE.BASE_QUANTITY" | translate }}
                    </p>
                    <p class="crm-mobile-card__value">{{ line.baseQuantity | number: "1.0-4" }}</p>
                  </div>
                </div>
              </article>
            }
          }
        </div>
      </app-card-shell>
    }
  </div>

  <app-reusable-modal
    [isOpen]="isMarkShippedModalOpen"
    [panelClass]="'max-w-lg'"
    [title]="'COMPONENTS.SHIPMENTS.MODAL.MARK_SHIPPED_TITLE' | translate"
    [subtitle]="'COMPONENTS.SHIPMENTS.MODAL.MARK_SHIPPED_PROMPT' | translate"
    (closed)="closeMarkShippedModal()"
  >
    <div class="shipment-overview-page__confirm-actions">
      <button
        type="button"
        class="form-secondary-btn"
        [disabled]="isMutating"
        (click)="closeMarkShippedModal()"
      >
        {{ "BUTTONS.CANCEL" | translate }}
      </button>
      <button
        type="button"
        class="form-primary-btn"
        [disabled]="isMutating"
        (click)="confirmMarkShipped()"
      >
        @if (isMutating) {
          {{ "COMPONENTS.SHIPMENTS.BUTTONS.MARKING_SHIPPED" | translate }}
        } @else {
          {{ "COMPONENTS.SHIPMENTS.BUTTONS.MARK_SHIPPED" | translate }}
        }
      </button>
    </div>
  </app-reusable-modal>

  <app-reusable-modal
    [isOpen]="isDeleteModalOpen"
    [panelClass]="'max-w-lg'"
    [title]="'COMPONENTS.SHIPMENTS.MODAL.DELETE_TITLE' | translate"
    [subtitle]="'COMPONENTS.SHIPMENTS.MODAL.DELETE_PROMPT' | translate"
    (closed)="closeDeleteModal()"
  >
    <div class="shipment-overview-page__confirm-actions">
      <button
        type="button"
        class="form-secondary-btn"
        [disabled]="isMutating"
        (click)="closeDeleteModal()"
      >
        {{ "BUTTONS.CANCEL" | translate }}
      </button>
      <button
        type="button"
        class="form-danger-btn"
        [disabled]="isMutating"
        (click)="confirmDelete()"
      >
        @if (isMutating) {
          {{ "COMPONENTS.SHIPMENTS.BUTTONS.DELETING" | translate }}
        } @else {
          {{ "COMPONENTS.SHIPMENTS.BUTTONS.DELETE" | translate }}
        }
      </button>
    </div>
  </app-reusable-modal>
</div>
